/**
 * Go Fish AI - Memory-based card tracking
 * Tracks opponent asks and makes intelligent decisions
 */

type Rank = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K'
type Card = { rank: Rank; suit: string }

export class GoFishAI {
  private opponentHand: Map<Rank, number> // Track what opponent likely has
  private askedByOpponent: Set<Rank> // Ranks opponent has asked for
  private deniedRanks: Set<Rank> // Ranks we asked for but opponent said "Go Fish"
  private myHand: Card[]

  constructor() {
    this.opponentHand = new Map()
    this.askedByOpponent = new Set()
    this.deniedRanks = new Set()
    this.myHand = []
  }

  /**
   * Set AI's current hand
   */
  setHand(cards: Card[]): void {
    this.myHand = cards
  }

  /**
   * Record that opponent asked for a rank
   */
  recordOpponentAsk(rank: Rank, weHadIt: boolean): void {
    this.askedByOpponent.add(rank)
    if (weHadIt) {
      // Opponent likely has this rank (since they asked)
      const current = this.opponentHand.get(rank) || 0
      this.opponentHand.set(rank, current + 1)
    }
  }

  /**
   * Record opponent's response to our ask
   */
  recordOpponentResponse(rank: Rank, theyHadIt: boolean, _cardsReceived: number = 0): void {
    if (theyHadIt) {
      // They had it, so remove from our tracking
      this.opponentHand.delete(rank)
    } else {
      // They said "Go Fish"
      this.deniedRanks.add(rank)
    }
  }

  /**
   * Choose which rank to ask for
   */
  chooseRankToAsk(): Rank {
    // Count ranks in our hand
    const rankCounts = new Map<Rank, number>()
    for (const card of this.myHand) {
      rankCounts.set(card.rank, (rankCounts.get(card.rank) || 0) + 1)
    }

    // Strategy 1: Ask for ranks we have multiple of (to complete books)
    const ranksWithMultiple: Rank[] = []
    for (const [rank, count] of rankCounts.entries()) {
      if (count >= 2 && count < 4) {
        ranksWithMultiple.push(rank)
      }
    }

    if (ranksWithMultiple.length > 0) {
      // Prefer ranks opponent asked for (they likely have them)
      for (const rank of ranksWithMultiple) {
        if (this.askedByOpponent.has(rank)) {
          return rank
        }
      }
      // Otherwise, pick the one with most cards
      ranksWithMultiple.sort((a, b) => (rankCounts.get(b) || 0) - (rankCounts.get(a) || 0))
      return ranksWithMultiple[0]
    }

    // Strategy 2: Ask for ranks opponent likely has
    for (const rank of this.askedByOpponent) {
      if (rankCounts.has(rank) && !this.deniedRanks.has(rank)) {
        return rank
      }
    }

    // Strategy 3: Ask for any rank we have
    const availableRanks = Array.from(rankCounts.keys()).filter(
      rank => !this.deniedRanks.has(rank)
    )

    if (availableRanks.length > 0) {
      return availableRanks[Math.floor(Math.random() * availableRanks.length)]
    }

    // Fallback: Random rank from hand
    return this.myHand[Math.floor(Math.random() * this.myHand.length)].rank
  }

  /**
   * Decide whether to go fish or ask (when prompted)
   */
  shouldAsk(): boolean {
    return this.myHand.length > 0
  }

  /**
   * Record that opponent made a book
   */
  recordOpponentBook(rank: Rank): void {
    this.opponentHand.delete(rank)
    this.askedByOpponent.delete(rank)
    this.deniedRanks.delete(rank)
  }

  /**
   * Reset AI state for new game
   */
  reset(): void {
    this.opponentHand.clear()
    this.askedByOpponent.clear()
    this.deniedRanks.clear()
    this.myHand = []
  }

  /**
   * Get AI's confidence level (for debugging/UI)
   */
  getConfidence(): number {
    // Higher confidence if we've learned more about opponent
    const knownInfo = this.askedByOpponent.size + this.deniedRanks.size
    return Math.min(100, knownInfo * 10)
  }
}
